import React, { useEffect, useState } from "react";
import { Modal, Table, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchPlanFeatures,
    addFeaturesToPlan,
    updatePlanFeature,
    removePlanFeature,
} from "../../../redux/slice/subscriptionSlice";
import { useLoader } from "../../../context/LoaderContext";
import { Cancel01Icon, TaskEditIcon, CheckIcon, DeleteIcon } from "../../../assets/icons";
import CommonButton from "../../../components/commonButton";
import withModal from "../../../components/withModal";
import ConfirmModal from "../../../components/withModal/ConfirmModal";
import { useToast } from "../../../context/TostContext";
import StatusChip from "../../../components/statusChip/StatusChip";

import CustomSelect from "../../../components/form/CustomSelect";
import InputField from "../../../components/form/InputField";

const ManageFeatures = ({ onClose, plan }) => {
    const dispatch = useDispatch();
    const { setLoading } = useLoader();
    const toast = useToast();
    const { planFeatures, features } = useSelector((state) => state.subscription);

    const [selectedFeature, setSelectedFeature] = useState(null);
    const [featureValue, setFeatureValue] = useState("");
    const [editingFeatureId, setEditingFeatureId] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [editStatus, setEditStatus] = useState("active");

    const [confirmModalConfig, setConfirmModalConfig] = useState({
        isOpen: false,
        title: "",
        message: "",
        onConfirm: () => { },
        confirmText: "Yes",
        cancelText: "No",
    });

    useEffect(() => {
        if (plan?.id) {
            dispatch(fetchPlanFeatures(plan.id));
        }
    }, [plan?.id, dispatch]);

    const handleAddFeature = async () => {
        if (!selectedFeature) return;

        let value = featureValue;
        const fType = selectedFeature.value_type;

        if (fType === "number") value = Number(value);
        if (fType === "boolean") value = value === "true";
        if (fType === "json") {
            if (Array.isArray(value)) {
                value = value.reduce((acc, curr) => {
                    if (curr.k.trim()) acc[curr.k.trim()] = curr.v;
                    return acc;
                }, {});
            } else if (typeof value === 'string') {
                try {
                    value = JSON.parse(value);
                } catch (e) {
                    toast.error("Invalid JSON format");
                    return;
                }
            }
        }

        setLoading(true);
        try {
            const result = await dispatch(
                addFeaturesToPlan({
                    planId: plan.id,
                    data: {
                        features: [{ feature_id: selectedFeature.value, value }],
                    },
                })
            ).unwrap();

            if (result?.success) {
                toast.success(result.message || "Feature linked successfully");
                setSelectedFeature(null);
                setFeatureValue("");
                dispatch(fetchPlanFeatures(plan.id));
            }
        } catch (err) {
            toast.error(err?.message || "Failed to link feature");
        } finally {
            setLoading(false);
        }
    };

    const confirmRemove = async (featureId) => {
        setLoading(true);
        try {
            const result = await dispatch(removePlanFeature({ planId: plan.id, featureId })).unwrap();
            if (result.res.success) {
                toast.success(result.res.message || "Feature removed");
            }
        } catch (err) {
            toast.error(err?.message || "Failed to remove feature");
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = (featureId) => {
        setConfirmModalConfig({
            isOpen: true,
            title: "Remove Feature",
            message: "Are you sure you want to remove this feature from plan?",
            confirmText: "Remove",
            cancelText: "Cancel",
            onConfirm: () => confirmRemove(featureId),
        });
    };

    const handleUpdateValue = async (featureId, newValue, valueType, newStatus) => {
        let value = newValue;
        if (newValue !== undefined) {
            if (valueType === "number") value = Number(value);
            if (valueType === "boolean") value = newValue === "true";
            if (valueType === "json" && Array.isArray(value)) {
                value = value.reduce((acc, curr) => {
                    if (curr.k.trim()) acc[curr.k.trim()] = curr.v;
                    return acc;
                }, {});
            }
        }

        setLoading(true);
        try {
            const data = {};
            if (value !== undefined) data.value = value;
            if (newStatus !== undefined) data.status = newStatus;

            const result = await dispatch(updatePlanFeature({
                planId: plan.id,
                featureId,
                data
            })).unwrap();

            if (result.res.success) {
                toast.success(result.res.message || "Updated successfully");
                setEditingFeatureId(null);
            }
        } catch (err) {
            toast.error(err?.message || "Failed to update");
        } finally {
            setLoading(false);
        }
    };


    // Filter out features already added to this plan
    const availableFeatures = features
        .filter(f => !planFeatures.some(pf => pf.feature_id === f.id))
        .map(f => ({
            value: f.id,
            label: `${f.name} (${f.code})`,
            value_type: f.value_type,
        }));

    const renderValueInput = (type, value, onChange) => {
        if (type === "boolean") {
            return (
                <Form.Check
                    type="switch"
                    checked={Boolean(value)}
                    onChange={(e) => onChange(e.target.checked)}
                    className="mt-1 shadow-none custom-plan-switch"
                />
            );
        }
        if (type === "number") {
            return (
                <Form.Control
                    size="sm"
                    type="number"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
        }
        if (type === "json") {
            const data = Array.isArray(value) ? value : [];
            return (
                <div className="border rounded p-2 bg-white" style={{ minWidth: '220px' }}>
                    {data.map((pair, index) => (
                        <div key={index} className="d-flex gap-1 mb-1 align-items-center">
                            <Form.Control
                                size="sm"
                                placeholder="Key"
                                value={pair.k}
                                onChange={(e) => {
                                    const newData = [...data];
                                    newData[index] = { ...newData[index], k: e.target.value };
                                    onChange(newData);
                                }}
                            />
                            <Form.Control
                                size="sm"
                                placeholder="Value"
                                value={pair.v}
                                onChange={(e) => {
                                    const newData = [...data];
                                    newData[index] = { ...newData[index], v: e.target.value };
                                    onChange(newData);
                                }}
                            />
                            <button className="cursor-pointer text-danger bg-transparent border-0" onClick={() => {
                                const newData = data.filter((_, i) => i !== index);
                                onChange(newData);
                            }}>
                                <Cancel01Icon size={14} />
                            </button>
                        </div>
                    ))}
                    <div
                        className="text-primary small cursor-pointer fw-semibold mt-1"
                        style={{ fontSize: '11px' }}
                        onClick={() => {
                            onChange([...data, { k: "", v: "" }]);
                        }}
                    >
                        + Add Pair
                    </div>
                </div>
            );
        }
        return (
            <Form.Control
                size="sm"
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        );
    };

    return (<>
        <Modal.Body>
            <div className="bg-light p-3 rounded-2 mb-4">
                <h6>Add Feature to Plan</h6>
                <div className="grid align-items-end mt-2">

                    <CustomSelect
                        label={{ name: "Select Feature" }}
                        options={availableFeatures}
                        value={selectedFeature}
                        onChange={(val) => {
                            setSelectedFeature(val);
                            let initialVal = "";
                            if (val?.value_type === "boolean") initialVal = "true";
                            if (val?.value_type === "json") initialVal = {};
                            setFeatureValue(initialVal);
                        }}
                        placeholder="Choose feature..."
                        className="g-col-4"
                    />

                    {selectedFeature && ["boolean", "json"].includes(selectedFeature.value_type) ? (
                        <div className="g-col-4">
                            <Form.Label className="small">Value</Form.Label>
                            {renderValueInput(selectedFeature.value_type, featureValue, setFeatureValue)}
                        </div>
                    ) : (
                        <InputField
                            className="g-col-4"
                            label={{ name: "Value" }}
                            input={{
                                placeholder: selectedFeature ? "Enter value" : "Select feature first",
                                disabled: !selectedFeature,
                                type: selectedFeature?.value_type === "number" ? "number" : "text",
                                value: featureValue,
                                onChange: (e) => setFeatureValue(e.target.value),
                            }}
                        />
                    )}

                    <CommonButton
                        title="Link Feature"
                        className="g-col-4"
                        variant="primary"
                        onClick={handleAddFeature}
                        disabled={!selectedFeature}

                    />

                </div>
            </div>

            <Table className="custom-table border mt-2">
                <thead>
                    <tr>
                        <th className="text-start">Feature</th>
                        <th>Type</th>
                        <th>Value</th>
                        <th>Status</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {planFeatures.length === 0 ? (
                        <tr><td colSpan={5} className="text-center py-4">No features linked to this plan yet.</td></tr>
                    ) : (
                        planFeatures.map((pf) => (
                            <tr key={pf.id}>
                                <td className="text-start">
                                    <strong>{pf.feature?.name}</strong>
                                    <p className="text-muted fs-sm">{pf.feature?.code}</p>
                                </td>
                                <td className="text-capitalize">{pf.feature?.value_type}</td>
                                <td style={{ minWidth: pf.feature?.value_type === 'json' ? '220px' : '80px' }}>
                                    {editingFeatureId === pf.feature_id ? (
                                        renderValueInput(
                                            pf.feature?.value_type,
                                            editValue,
                                            setEditValue
                                        )
                                    ) : (
                                        <span className="fw-semibold">
                                            {pf.feature?.value_type === "boolean" ? (pf.value ? "True" : "False") :
                                                pf.feature?.value_type === "json" ? JSON.stringify(pf.value) : String(pf.value)}
                                        </span>
                                    )}
                                </td>
                                <td>
                                    <StatusChip
                                        bg={(editingFeatureId === pf.feature_id ? editStatus === "active" : pf.status === "active") ? "success" : "danger"}
                                        pill
                                        title={(editingFeatureId === pf.feature_id ? editStatus === "active" : pf.status === "active") ? "Active" : "Inactive"}
                                        className="cursor-pointer"
                                        onClick={() => {
                                            if (editingFeatureId === pf.feature_id) {
                                                const status = editStatus === "active" ? "inactive" : "active";
                                                setEditStatus(status);
                                            } else {
                                                const isActive = pf.status === "active";
                                                setConfirmModalConfig({
                                                    isOpen: true,
                                                    title: isActive ? "Deactivate Feature" : "Activate Feature",
                                                    message: isActive
                                                        ? "This feature will be deactivated. Do you want to continue?"
                                                        : "This feature will be activated. Do you want to continue?",
                                                    confirmText: "Yes",
                                                    cancelText: "No",
                                                    onConfirm: () => {
                                                        const newStatus = isActive ? "inactive" : "active";
                                                        handleUpdateValue(pf.feature_id, undefined, undefined, newStatus);
                                                    },
                                                });
                                            }
                                        }}
                                    />
                                </td>
                                <td>
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                        {editingFeatureId === pf.feature_id ? (
                                            <>
                                                <CommonButton icon={<CheckIcon size={18} />} className="p-2" onClick={() => handleUpdateValue(pf.feature_id, editValue, pf.feature?.value_type, editStatus)} variant="outline-success" />
                                                <CommonButton icon={<DeleteIcon size={18} />} className="p-2" onClick={() => setEditingFeatureId(null)} variant="outline-danger" />
                                            </>
                                        ) : (
                                            <>
                                                <CommonButton icon={<TaskEditIcon size={18} />} className="p-2" onClick={() => {
                                                    setEditingFeatureId(pf.feature_id);
                                                    let val = pf.value;
                                                    if (pf.feature?.value_type === 'json') {
                                                        val = Object.entries(pf.value || {}).map(([k, v]) => ({ k, v }));
                                                    }
                                                    setEditValue(val);
                                                    setEditStatus(pf.status);
                                                }} variant="outline-primary" />
                                                <CommonButton icon={<DeleteIcon size={18} />} className="p-2" onClick={() => handleRemove(pf.feature_id)} variant="outline-danger" />
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
        </Modal.Body>
        <ConfirmModal
            isOpen={confirmModalConfig.isOpen}
            onClose={() => setConfirmModalConfig((prev) => ({ ...prev, isOpen: false }))}
            title={confirmModalConfig.title}
            message={confirmModalConfig.message}
            onConfirm={confirmModalConfig.onConfirm}
            confirmText={confirmModalConfig.confirmText}
            cancelText={confirmModalConfig.cancelText}
            className="confirm-modal"
        />
    </>);
};

export default withModal(ManageFeatures);
